import React, { useState } from 'react';
import { Phone, Mail, MapPin, ShieldCheck, Send, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useContent } from '../hooks/useContent';
import Hero from '../components/ui/Hero';
import SEO from '../components/SEO';

const Contact = () => {
  const { t } = useLanguage();
  const { data: pagesData, loading: pagesLoading } = useContent('pages_content');
  const { data: contactData, loading: contactLoading } = useContent('contact_info');
  const [formStatus, setFormStatus] = useState('idle'); // idle, sending, success, error

  const page = pagesData?.find(p => p.identifier === 'kontakt');
  const contact = contactData?.[0] || {};

  // Fallbacks to ensure contact info is ALWAYS visible even if CMS is empty
  const personName = contact.person_name || "Karsten Andersen";
  const personImage = contact.person_image?.[0] || "https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/cea601ba-c74d-47ba-9a6d-34aad97a5c88.jpg";
  const phone = contact.phone || "21 59 00 19";
  const email = contact.email || "kra@railmaskinteknik.dk";
  const cvr = contact.cvr || "44117568";
  const address = contact.address || { da: 'Rail & Maskinteknik ApS, Danmark', en: 'Rail & Maskinteknik ApS, Denmark' };

  if (pagesLoading || contactLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setFormStatus('success');
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <main>
      <SEO 
        title={t({ da: 'Kontakt', en: 'Contact', de: 'Kontakt' })} 
        description={t({ 
          da: 'Kontakt Rail & Maskinteknik ApS for professionel service og rådgivning.', 
          en: 'Contact Rail & Maskinteknik ApS for professional service and advisory.',
          de: 'Kontaktieren Sie Rail & Maskinteknik ApS für professionellen Service und Beratung.' 
        })} 
      />
      <Hero 
        title={page?.hero_title || { da: 'Kontakt', en: 'Contact', de: 'Kontakt' }} 
        subtitle={page?.hero_subtitle || { da: 'Kontakt os for en uforpligtende snak', en: 'Contact us for a non-binding talk', de: 'Kontaktieren Sie uns für ein unverbindliches Gespräch' }}
        image="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/2cbfb6aa-acdd-4b8c-b04b-54a666316eea.jpg"
        isSubpage={true}
      />

      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-12">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                {personImage && (
                  <div className="w-48 h-60 rounded-2xl overflow-hidden shadow-lg flex-shrink-0">
                    <img
                      src={personImage}
                      alt={personName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-1">{personName}</h3>
                    <p className="text-slate-500 font-medium uppercase tracking-wider text-xs">
                      {t({ da: 'Ejer & Direktør', en: 'Owner & Director', de: 'Inhaber & Geschäftsführer' })}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <a
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      className="flex items-center gap-4 text-slate-700 hover:text-slate-900 transition-colors group"
                    >
                      <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center group-hover:bg-slate-100 transition-colors">
                        <Phone className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium">{phone}</span>
                    </a>
                    <a
                      href={`mailto:${email}`}
                      className="flex items-center gap-4 text-slate-700 hover:text-slate-900 transition-colors group"
                    >
                      <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center group-hover:bg-slate-100 transition-colors">
                        <Mail className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium">{email}</span>
                    </a>
                    <div className="flex items-center gap-4 text-slate-700">
                      <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium">CVR: {cvr}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-slate-50 rounded-3xl border border-slate-100">
                <h4 className="text-xl font-bold text-slate-900 mb-4">
                  {t({ da: 'Vores kontor', en: 'Our Office', de: 'Unser Büro' })}
                </h4>
                <div className="flex items-start gap-4 text-slate-600">
                  <MapPin className="w-6 h-6 mt-1 flex-shrink-0" />
                  <p className="text-lg leading-relaxed">
                    {t(address)}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div
              className="bg-slate-50 p-5 md:p-12 rounded-3xl border border-slate-100 shadow-sm"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-8">
                {t({ da: 'Send os en besked', en: 'Send us a message', de: 'Senden Sie uns eine Nachricht' })}
              </h3>

              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 mb-2">
                    {t({ da: 'Besked sendt!', en: 'Message sent!', de: 'Nachricht gesendet!' })}
                  </h4>
                  <p className="text-slate-600">
                    {t({ 
                      da: 'Tak for din henvendelse. Vi vender tilbage hurtigst muligt.', 
                      en: 'Thank you for your inquiry. We will get back to you as soon as possible.',
                      de: 'Vielen Dank für Ihre Anfrage. Wir werden uns so schnell wie möglich bei Ihnen melden.' 
                    })}
                  </p>
                  <button 
                    onClick={() => setFormStatus('idle')}
                    className="mt-8 text-slate-900 font-bold underline"
                  >
                    {t({ da: 'Send en ny besked', en: 'Send another message', de: 'Eine weitere Nachricht senden' })}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot */}
                  <div className="sr-only absolute">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" id="website_url" name="website_url" tabIndex={-1} autoComplete="off" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                        {t({ da: 'Navn', en: 'Name', de: 'Name' })}
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all"
                        placeholder={t({ da: 'Dit fulde navn', en: 'Your full name', de: 'Ihr vollständiger Name' })}
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                        {t({ da: 'E-mail', en: 'Email', de: 'E-Mail' })}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all"
                        placeholder="mail@eksempel.dk"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="subject" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                      {t({ da: 'Emne', en: 'Subject', de: 'Betreff' })}
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all"
                      placeholder={t({ da: 'Hvad drejer det sig om?', en: 'What is it about?', de: 'Worum geht es?' })}
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                      {t({ da: 'Besked', en: 'Message', de: 'Nachricht' })}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all resize-none"
                      placeholder={t({ da: 'Skriv din besked her...', en: 'Write your message here...', de: 'Schreiben Sie Ihre Nachricht hier...' })}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full py-4 bg-[#f59c00] text-white font-bold rounded-xl hover:bg-[#e08e00] transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {formStatus === 'sending' ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>
                        {t({ da: 'Send besked', en: 'Send message', de: 'Nachricht senden' })}
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  
                  {formStatus === 'error' && (
                    <p className="text-red-600 text-sm font-medium text-center">
                      {t({ 
                        da: 'Der opstod en fejl. Prøv venligst igen eller ring til os.', 
                        en: 'An error occurred. Please try again or call us.',
                        de: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns an.' 
                      })}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
